import { Injectable } from "@angular/core";
import { Apollo, ResultOf } from "apollo-angular";
import { ApolloLink } from "@apollo/client";

import { GET_INSPECTION_EXECUTION_DATA_BY_ID, GET_USER_ZONE_ASSIGNMENTS_BY_USER_EMAIL } from "../queries/qbuilt-queries";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { DELETE_CACHED_INSPECTION, GET_ALL_DEPARTMENTS, GET_ALL_IN_USE_SERIAL_NUMBERS, GET_ALL_LOCATIONS, GET_CUSTOM_TASK_GROUPS, GET_DEFECT_STATUSES, GET_INSPECTION_PROGRESS, INITIATE_INSPECTION, SAVE_INSPECTION_COMPLETION, SAVE_INSPECTION_PROGRESS } from "src/app/modules/qbuilt/queries/qbuilt.queries";
import { MutationResult, UpdateResult } from "src/app/shared/graph-queries/types";
import { firstValueFrom, map, Observable } from "rxjs";
import { InspectionCompletionModel, InspectionInitiationModel } from "../models/qbuilt-inspector.models";
import { CustomTaskGroupPayload } from "src/app/modules/qbuilt/models/customTaskGroupParams";
import { QBuiltInspectorConstants } from "../models/constants/qbuilt-inspector.constants";
import { HbgSnackBar } from "src/app/shared/toaster/hbg-toaster";

@Injectable({
  providedIn: 'root',
})

export class QBuiltInspectorService {
  cqmsSessionKey: string = this.qbuiltInspConstants.cqmsItemsKey;

  constructor(private apolloClient: Apollo, private qbuiltInspConstants: QBuiltInspectorConstants, private toaster: HbgSnackBar) {}

  private userData = new BehaviorSubject<any>(null);
  userData$ = this.userData.asObservable();

  private inspectionData = new BehaviorSubject<any>(null);
  inspectionData$ = this.inspectionData.asObservable();

  private customGroups = new BehaviorSubject<any>(null);
  customGroups$ = this.customGroups.asObservable();

  private locations = new BehaviorSubject<any>(null);
  locations$ = this.locations.asObservable();

  private departments = new BehaviorSubject<any>(null);
  departments$ = this.departments.asObservable();

  private defectStatuses = new BehaviorSubject<any>(null);
  defectStatuses$ = this.defectStatuses.asObservable();
  private inUseSerials = new BehaviorSubject<any>(null);
  inUseSerials$ = this.inUseSerials.asObservable();

  private inspectionInitiationResponse = new BehaviorSubject<any>(null);
  inspectionInitiationResponse$ = this.inspectionInitiationResponse.asObservable();

  private newInspection = new BehaviorSubject<any>(null);
  newInspection$ = this.newInspection.asObservable();

  private activeInspectionsData = new BehaviorSubject<any>(null);
  activeInspectionsData$ = this.activeInspectionsData.asObservable();

  private inspectionProgressData = new BehaviorSubject<any>(null);
  inspectionProgressData$ = this.inspectionProgressData.asObservable();

  booleanList = new BehaviorSubject<boolean[]>([]);
  booleanList$ = this.booleanList.asObservable();

  
  getUserData(userEmail: string): void {
    this.apolloClient
    .query({
        query: GET_USER_ZONE_ASSIGNMENTS_BY_USER_EMAIL,
        variables: { userEmail },
        fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
        this.userData.next(response.data.GetUserZoneAssignmentsByUserEmail);
    });
  }

  getInspectionExecutionData(inspectionId: string): void {
    this.apolloClient.query({
      query: GET_INSPECTION_EXECUTION_DATA_BY_ID,
      variables: { id: inspectionId },
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.inspectionData.next(response.data.GetInspectionExecutionData);
    });
  }

  saveInspectionInitiation(inspectionInit : InspectionInitiationModel): void {
     this.apolloClient.mutate<UpdateResult>({
      mutation: INITIATE_INSPECTION,
      variables: {
          payload: JSON.stringify(inspectionInit)},
          fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.inspectionInitiationResponse.next(response.data.InitiateInspection);
    });
  }
    
saveNewInspection(inspectionInit: InspectionInitiationModel): Observable<any> {
  return this.apolloClient.mutate({
    mutation: INITIATE_INSPECTION,
    variables: {
      payload: JSON.stringify(inspectionInit)
    },
    fetchPolicy: 'no-cache'
  }).pipe(
    map((response: any) => {
      const result = response?.data?.InitiateInspection;
      if (result) {
        // Do NOT emit to inspectionInitiationResponse here — saveNewInspection() is consumed
        // directly via its returned Observable (in addNewSerial). Emitting here causes the
        // stale getNewSerialData() subscription from ngOnInit to fire and create a duplicate tab.
      }
      return result;
    })
  );
}

clearInspectionInitiationResponse(): void {
  this.inspectionInitiationResponse.next(null);
}

clearInspectionProgressData(): void {
  this.inspectionProgressData.next(null);
}

async saveInspectionCompletionResults(
  inspectionCompletion: InspectionCompletionModel
): Promise<MutationResult<any>> {
  return await firstValueFrom(
    this.apolloClient.mutate<UpdateResult>({
      mutation: SAVE_INSPECTION_COMPLETION,
      variables: {
        payload: JSON.stringify(inspectionCompletion)
      },
    })
  );
}

async saveInspectionProgress(payload: any): Promise<MutationResult<any>> {
  return await firstValueFrom(
    this.apolloClient.mutate<UpdateResult>({
      mutation: SAVE_INSPECTION_PROGRESS,
      variables: {
        payload: JSON.stringify(payload)
      },
      fetchPolicy: 'no-cache'
    })
  );
}

async deleteInspectionProgress(id: string): Promise<MutationResult<any>> {
  return await firstValueFrom(
    this.apolloClient.mutate<UpdateResult>({
      mutation: DELETE_CACHED_INSPECTION,
      variables: {
        payload: id
      },
      fetchPolicy: 'no-cache'
    })
  );
}

  getInspectionProgressData(filter: string): void {
     this.apolloClient.query({
      query: GET_INSPECTION_PROGRESS,
      variables: { filter },
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.inspectionProgressData.next(response.data.GetInspectionProgress);
    });
  }

    getCustomTaskGroups(payload: CustomTaskGroupPayload): void {
    this.apolloClient.query({
      query: GET_CUSTOM_TASK_GROUPS,
      variables: { payload: JSON.stringify({ GroupName: payload.GroupName, FacilityNumber: payload.FacilityNumber }) },
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.customGroups.next(response.data.GetCustomTaskGroup);
    });
  }

  getInspectionDepartments(): void {
    this.apolloClient.query({
      query: GET_ALL_DEPARTMENTS,
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.departments.next(response.data.GetInspectionDepartments);
    });
  }

  GetInspectionLocations(): void {
    this.apolloClient.query({
      query: GET_ALL_LOCATIONS,
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.locations.next(response.data.GetInspectionLocations);
    });
  }

    GetInspectionDefectStatuses(): void {
    this.apolloClient.query({
      query: GET_DEFECT_STATUSES,
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.defectStatuses.next(response.data.GetInspectionDefectStatuses);
    });
  }
    GetAllInUseSerials(userFacility: string, zoneId: string): void {
    this.inUseSerials.next(null);
    this.apolloClient.query({
      query: GET_ALL_IN_USE_SERIAL_NUMBERS,
      variables: { userFacility, zoneId },
      fetchPolicy: 'no-cache',
    }).subscribe((response: any) => {
      this.inUseSerials.next(response.data.GetAllInUseSerials);
    });
  }

//method used to save data to the dynamo database -- called across the application in various places
async saveInspectionProgressToDynamo(
  plantNumber: string, 
  zoneSelection: string, 
  currentTab: any[]
): Promise<boolean> {
  if(!currentTab?.length) {
    this.toaster.error('Save Progress Error, Please Try Again or Refresh Page' , 'Close', { duration: 3000 });
    return false;
  }

  if (!plantNumber || !zoneSelection) {
    this.toaster.error('Save Progress Error, Please Try Again or Refresh Page' , 'Close', { duration: 3000 });
    return false;
  }

  const data = {
    Pk: `${plantNumber}-${zoneSelection}`,
    InspectionData: currentTab
  };

  
  try {
    const res = await this.saveInspectionProgress(data);
    if(res.data.SaveInspectionProgress.Message.includes('Validation error')) {
      this.toaster.error('Save Progress Validation Error, please return to QBUILT landing page and re-open active inspections -- there may be loss of data' , 'Close', { duration: 3000 });
      return false;
    } else {
      const result = res.data.SaveInspectionProgress.UpdatedData;
      const inspectionProgress = JSON.parse(result);
      const inspectionData = inspectionProgress.InspectionData;
      const cqmsSessionItems = sessionStorage.getItem(this.cqmsSessionKey) ? JSON.parse(sessionStorage.getItem(this.cqmsSessionKey) || '{}') : {};

      sessionStorage.setItem(this.cqmsSessionKey, JSON.stringify(cqmsSessionItems));
      const zoneName = sessionStorage.getItem(this.cqmsSessionKey) ? JSON.parse(sessionStorage.getItem(this.cqmsSessionKey) || '{}').zoneName : '';
      cqmsSessionItems.currentTabList = inspectionData;
      sessionStorage.setItem(this.cqmsSessionKey, JSON.stringify(cqmsSessionItems));
      this.toaster.open('Save Progress Successful in ' + zoneName , 'Close', { duration: 3000 });
      return true;
    }
  } catch (err) {
    console.error('progress save error', err);
    this.toaster.error('Save Progress Error, Please Try Again or Refresh Page' , 'Close', { duration: 3000 });
    return false;
  }
}

async deleteInspectionProgressFromDynamo(id: string): Promise<any> {
  const res = await this.deleteInspectionProgress(id);
  const raw = res?.data?.DeleteCachedInspection?.UpdatedData ?? null;
  
  // Safe parse
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try { 
        return JSON.parse(trimmed); 
      } catch { 
        return { message: trimmed }; 
      }
    }
    return { message: trimmed };
  }
  return raw;
}

}